import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Store } from '@ngrx/store';
import { Map, Set } from 'immutable';
import { BehaviorSubject, map, Observable, takeUntil } from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { ColumnConfigItemEntity } from '../../../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../../../domain/column-model-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../../domain/diagnostic-errors.entity';
import { FilterModelItemEntity } from '../../../domain/filter-model-item.entity';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import {
	refreshColumnModelsForSimulation,
	refreshFilterModelsForSimulation,
	SubscriptionSimulatorSelector,
} from '../../../store';
import { ColumnModelsComponent } from '../../column-models/column-models.component';
import { FilterModelsComponent } from '../../filter-models/filter-models.component';

enum ModelsSelectionStep {
	ColumnModel = 0,
	FilterModel = 1,
}

@Component({
	selector: 'app-models-selection-popup',
	templateUrl: './models-selection-modal.component.html',
	styleUrls: ['./models-selection-modal.component.scss'],
	providers: [SubscriptionProvider],
})
export class ModelsSelectionModalComponent<
	T extends {
		selectedColumnModelId: string;
		selectedFilterModelIds: Set<string>;
	}
> extends DcBaseComponent {
	protected readonly ModelsSelectionStep = ModelsSelectionStep;

	@ViewChild(ColumnModelsComponent)
	private readonly columnModelsCmp: ColumnModelsComponent | null = null;

	@ViewChild(FilterModelsComponent)
	private readonly filterModelsCmp: FilterModelsComponent | null = null;

	public vo$: Observable<{
		currentStep: ModelsSelectionStep;
		columnModels: Map<string, ColumnModelItemEntity>;
		selectedColumnModelId: string;
		filterModels: Map<string, FilterModelItemEntity>;
		subscriptionColumns: Map<string, ColumnConfigItemEntity>;
		selectedFilterModelIds: Set<string>;
		errors: Map<SubscriptionErrorKey, SubscriptionDiagnosticError>;
		criticErrorCount: number;
	}>;

	private readonly currentStep$ = new BehaviorSubject<ModelsSelectionStep>(
		ModelsSelectionStep.ColumnModel
	);

	protected readonly SubscriptionErrorKey = SubscriptionErrorKey;
	protected readonly DcIcons = DcIcons;
	public constructor(
		private readonly store: Store,
		private readonly selector: SubscriptionSimulatorSelector,
		private readonly modalService: AppModalService,
		private readonly subscriptionProvider: SubscriptionProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'models-selection-modal';

		const subscriptionColumns$ = this.selector
			.getSubscriptionColumns$()
			.pipe(map((columns) => columns.filter((col) => col.isSelectedInSource)));

		this.vo$ = combineAll({
			currentStep: this.currentStep$,
			columnModels: this.selector.getColumnModelsForSimulation$(),
			selectedColumnModelId: this.modalData.selectedColumnModelId,
			filterModels: this.selector.getFilterModelsForSimulation$(),
			subscriptionColumns: subscriptionColumns$,
			selectedFilterModelIds: this.modalData.selectedFilterModelIds,
			errors: this.subscriptionProvider.errors$.pipe(
				map(() => this.subscriptionProvider.getOrderedErrors())
			),
			criticErrorCount: this.subscriptionProvider.errors$.pipe(
				map(() => this.subscriptionProvider.getCriticalErrors().size)
			),
		});
	}

	public refreshColumnModels(): void {
		this.store.dispatch(refreshColumnModelsForSimulation());
	}

	public refreshFilterModels(): void {
		this.store.dispatch(refreshFilterModelsForSimulation());
	}

	public setStep(step: ModelsSelectionStep): void {
		this.currentStep$.next(step);
	}

	public nextStep(): void {
		this.currentStep$.next(ModelsSelectionStep.FilterModel);
	}

	public previousStep(): void {
		this.currentStep$.next(ModelsSelectionStep.ColumnModel);
	}

	public launchAction(): void {
		if (!this.columnModelsCmp || !this.filterModelsCmp) {
			return;
		}

		const selectedColumnModels = this.columnModelsCmp.getSelectedColumnModels();
		const selectedFilterModels = this.filterModelsCmp.getSelectedFilterModels();

		this.modalService.closeActiveModal({
			columnModels: selectedColumnModels,
			filterModels: selectedFilterModels,
		});
	}
}
